/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { fetchShopForUser } from './authApi';
import { isSellerRole } from '../utils/roles';

const ShopContext = createContext();

function syncShopCredits(shop) {
  const shopCredits = shop?.credit_balance ?? null;
  if (shopCredits != null) {
    localStorage.setItem('shop_credits_balance', String(shopCredits));
    return;
  }

  localStorage.removeItem('shop_credits_balance');
}

function normalizeShop(shop) {
  if (!shop) return null;
  const normalized = { ...shop };

  if (typeof normalized.status === 'string') {
    normalized.status = normalized.status.toLowerCase();
  }

  if (normalized.credit_balance != null) {
    normalized.credit_balance = Number(normalized.credit_balance) || 0;
  }

  if (normalized.credit_used != null) {
    normalized.credit_used = Number(normalized.credit_used) || 0;
  }

  return normalized;
}

export function useShop() {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within ShopProvider');
  }
  return context;
}

export function ShopProvider({ children }) {
  const { currentRole, currentUser, loading: authLoading } = useAuth();
  const [shopDocument, setShopDocument] = useState(null);
  const [shopFetched, setShopFetched] = useState(false);
  const [shopLoading, setShopLoading] = useState(false);
  const [shopError, setShopError] = useState('');
  const requestSeqRef = useRef(0);

  const loadShop = useCallback(async (user, { silent = false } = {}) => {
    const requestSeq = requestSeqRef.current + 1;
    requestSeqRef.current = requestSeq;

    if (!silent) {
      setShopLoading(true);
      setShopFetched(false);
    }
    setShopError('');

    try {
      const shop = normalizeShop(await fetchShopForUser(user));
      if (requestSeqRef.current !== requestSeq) return null;

      syncShopCredits(shop);
      setShopDocument(shop);
      setShopError('');
      setShopFetched(true);
      return shop;
    } catch (error) {
      if (requestSeqRef.current !== requestSeq) return null;

      if (!silent) {
        syncShopCredits(null);
        setShopDocument(null);
      }

      setShopError(error?.message || 'Failed to load shop details.');
      setShopFetched(true);
      throw error;
    } finally {
      if (requestSeqRef.current === requestSeq) {
        setShopLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    requestSeqRef.current += 1;

    if (authLoading) {
      setShopError('');
      setShopLoading(false);
      setShopFetched(false);
      return;
    }

    if (!isSellerRole(currentRole)) {
      syncShopCredits(null);
      setShopDocument(null);
      setShopError('');
      setShopFetched(true);
      setShopLoading(false);
      return;
    }

    loadShop(currentUser).catch(() => null);
  }, [authLoading, currentRole, currentUser, loadShop]);

  const refreshShop = useCallback(async () => {
    if (!isSellerRole(currentRole)) return null;
    return loadShop(currentUser, { silent: true });
  }, [currentRole, currentUser, loadShop]);

  const value = useMemo(
    () => ({
      shopDocument,
      shopError,
      shopFetched,
      shopLoading,
      refreshShop,
      setShopDocument,
    }),
    [refreshShop, shopDocument, shopError, shopFetched, shopLoading]
  );

  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

export default ShopContext;
